<script setup>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import Input from "@/components/Input.vue";
import DateRangePicker from "@/components/DateRangePicker.vue";
import Card from "@/components/Card.vue";
import {useLocales} from "@/composables/locales";
import PageLoader from "@/components/PageLoader.vue";
import Button from "@/components/Button.vue";
import Img from "@/components/Img.vue";

import ScrollableListContainer from "@/components/ScrollableListContainer.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";

const store = useStore();

const {locale} = useLocales()


const activityStats = reactive({
  total_projects: 0,
  total_completed_tasks: 0,
  total_activities: 0
});
const loading = ref(false);
const search = ref("");
const query = ref({
  start_date: "",
  end_date: "",
  user_id: "",
  project_id: ""
});
const downloading = ref(false)
const activities = ref([])
const activitiesMeta = reactive({
  current_page: 1,
  last_page: 1,
  currentQuery: ''
})
const projects = ref([])


const collaborators = computed(() => store.getters["user/getUsers"]);
const selectedCollaboratorId = computed(() => query.value.user_id);
const filteredCollaborator = computed(() => {
  const q = search.value?.toString().toLowerCase() || ''

  if (!q) return collaborators.value

  return collaborators.value.filter(col =>
      col.fullname?.toString().toLowerCase().includes(q)
  )
})

watch(search, (newVal, oldVal) => {
  if (newVal !== oldVal && query.value) {
    query.value.user_id = ''
  }
})


watch(query,
    () => {
      if (query.value.start_date && query.value.end_date && query.value.user_id) {
        getCollaboratorActivities({...query.value, page: 1});
        getCollaboratorProjects({...query.value})
      }
    },
    {deep: true}
);


function parseCustomDate(dateString) {
  if (!dateString) return new Date();

  const [datePart, timePart] = dateString.split(" ");

  const [day, month, year] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);

  return new Date(year, month - 1, day, hours, minutes);
}


function getMonthName(monthNumber) {
  const monthNames = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december"
  ];
  return monthNames[monthNumber];
}

function handleRangeDate(range) {
  query.value.start_date = range.start_date;
  query.value.end_date = range.end_date;
}

function getCollaboratorProjects(query) {
  store.dispatch('projectLogs/loadCollaboratorProjects', query)
      .then(res => {
        projects.value = res.data.data
      })
      .catch()
}

function getCollaboratorActivities(query) {
  loading.value = true;
  const params = new URLSearchParams({
    start_date: query.start_date || '',
    end_date: query.end_date || '',
    user_id: query.user_id || '',
  })

  let filter = params.toString()

  store
      .dispatch("projectLogs/loadCollaboratorActivities", query)
      .then(res => {

        activityStats.total_projects = res.data?.total_projects || 0;
        activityStats.total_completed_tasks = res.data?.total_completed_tasks || 0;
        activityStats.total_activities = res.data?.total_actions || 0

        const data = res.data?.activities || [];

        const activitiesLogs = Array.isArray(data?.data) ? data?.data : Object.values(data?.data)

        if(activitiesMeta.currentQuery !== filter || data.current_page === activitiesMeta.current_page) {
          activities.value = activitiesLogs
        } else {
          activities.value = activities.value.concat(activitiesLogs)
        }
        activitiesMeta.current_page = data.current_page
        activitiesMeta.last_page = data.last_page
        activitiesMeta.currentQuery = filter
      })
      .finally(() => (loading.value = false));
}

const loadNextCollaboratorActivities = (page) => getCollaboratorActivities({...query.value, page});

function downloadLogs() {
  downloading.value = true
  store.dispatch("projectLogs/downloadUserActivitiesLog", query.value)
      .then(response => {
        let blob = new Blob([response.data], { type: response.headers['content-type'] });
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = "logs.pdf"
        link.click()
      })
      .finally(() => downloading.value = false);
}

onMounted(() => {
  store.dispatch("user/loadUsers");
});
</script>

<template>
  <div class="row">

    <div class="col-3 border-end border-gray-400 pe-3">
      <div class="card">
        <div class="card-header">
          <h6 class="card-title">{{ $t('performance.activities.collaborators') }}</h6>
        </div>

        <div class="card-body p-2">

          <Input
              container-class="form-group mb-5"
              name="search"
              type="text"
              :placeholder="$t('label.search')"
              labelClass="form-label fs-6 text-black"
              inputClass="form-control form-control-sm"
              v-model="search"
          />

          <ul class="list-group mt-3 scroll-y"  style="min-height: 20vh!important; max-height: 55vh;!important;" v-if="filteredCollaborator.length">
            <li
                v-for="c in filteredCollaborator"
                :key="c.id"
                class="list-group-item d-flex align-items-center bg-hover-primary text-hover-white"
                :class="{ active: c.id === selectedCollaboratorId }"
                @click="query.user_id = c.id"
                style="cursor:pointer;"
            >
              <div
                  class="symbol symbol-35px symbol-circle"
                  :title="c.fullname"
              >
                <span
                    class="symbol-label fw-bolder me-3"
                    :class="{
                    'bg-white text-primary': c.id === selectedCollaboratorId,
                    'bg-primary text-inverse-primary': c.id !== selectedCollaboratorId
                  }"
                >
                  {{ c.initials }}
                </span>
              </div>

              <span>{{ c.fullname }}</span>
            </li>
          </ul>

          <div v-else class="d-flex align-items-center justify-content-center" style="min-height: 20vh!important; max-height: 55vh;!important;">
            {{ $t('performance.activities.noCollaboratorFound') }}
          </div>

        </div>
      </div>
    </div>

    <div class="col-9 ps-4">
      <PageLoader v-if="loading" />
      <div v-if="!selectedCollaboratorId" class="d-flex align-items-center justify-content-center py-5 min-h-250px">
        {{ $t('performance.activities.noCollaboratorSelected') }}
      </div>

      <div v-else>
        <div class="d-flex mb-5">
          <DateRangePicker
              class="bg-white ms-0"
              id="dashboard-daterangepicker"
              @update:date="handleRangeDate"
          />
          <ButtonDropdownFilter
              btnClass="bg-white text-hover-primary"
              :btnTitle="$t('performance.activities.allProject')"
              :search="true"
              :options="projects"
              value-field="id"
              name-field="title"
              v-model="query.project_id"
          />
          <Button
              type="button"
              btn-class="btn-sm btn-primary ms-auto"
              btn-text="button.download"
              icon-class="fa fa-download fs-2"
              :is-loading="downloading"
              @click.prevent="downloadLogs" />
        </div>
        <div class="row mb-4">
          <div class="col">
            <div class="card p-3">
              <strong>{{ $t('performance.activities.nbrProject') }}</strong>
              {{ activityStats.total_projects }}
            </div>
          </div>

          <div class="col">
            <div class="card p-3">
              <strong>{{ $t('performance.activities.nbrTaskCompleted') }}</strong>
              {{ activityStats.total_completed_tasks }}
            </div>
          </div>

          <div class="col">
            <div class="card p-3">
              <strong> {{ $t('performance.activities.nbrAction') }}</strong>
              {{ activityStats.total_activities }}
            </div>
          </div>
        </div>

        <div>


          <div class="card mt-3">
            <div class="card-header">
              <h6 class="card-title">{{ $t('performance.activities.completedTask') }}</h6>
            </div>
            <ScrollableListContainer
                id="logs-scrollable-list-container"
                :loading="loading"
                :length="activities?.length"
                :empty-text="$t('reports.noData')"
                :current-page="activitiesMeta.current_page"
                :last-page="activitiesMeta.last_page"
                container-height="50vh"
                @load-next-page="loadNextCollaboratorActivities"
            >
              <template #empty>
                <div class="text-center">
                  <Img filename="empty.png" alt="" class="mw-100 h-150px" />
                  <div class="text-gray-400 fs-4 fw-bold">{{ $t('no_data_found') }}</div>
                </div>
              </template>
              <template #default>
                <div
                    v-for="historic in activities"
                    :key="historic.date"
                    class="timeline-item"
                >
                  <div class="timeline-line"></div>

                  <div class="timeline-icon">
                        <span>
                          {{ historic.date ? parseCustomDate(historic.date).getDate() : new Date().getDate() }}
                          {{
                            historic.date
                                ? $t(getMonthName(parseCustomDate(historic.date).getMonth())).substring(0,3)
                                : $t(getMonthName(new Date().getMonth())).substring(0,3)
                          }}
                        </span>
                    <span>
                          {{
                        historic.date
                            ? parseCustomDate(historic.date).getFullYear()
                            : new Date().getFullYear()
                      }}
                        </span>
                  </div>

                  <div class="timeline-content me-2">
                    <Card class="card shadow m-0 p-0">
                      <div class="card-body px-5 py-4">
                        <div class="fs-4" v-html="historic.action[locale]"></div>
                      </div>
                    </Card>
                  </div>
                </div>
              </template>
            </ScrollableListContainer>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.timeline-item {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.timeline-line {
  position: absolute;
  left: 25px;
  top: 50px;
  bottom: 0;
  border-left: 1px dashed #CCCFD3;
}

.timeline-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  text-align: center;
  font-weight: bold;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #CCCFD3;
  font-size: 8px !important;
}

.timeline-content {
  width: 100%;
  margin-left: 10px;
  margin-top: 50px;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  padding: 5px;
  background-color: #0072CE33;
  border: 1px solid #0072CE33;
}
</style>
